import React from "react";
import PropTypes from "prop-types";

const STAGE_LABEL = { LOOKUP: "Lookup", GATE: "Identity gate", BILLING: "Billing" };

function Bar({ label, value, total, tone }) {
    const widthPercent = total > 0 ? Math.max(2, Math.round((value / total) * 100)) : 0;
    return (
        <div className="flex items-center gap-3">
            <span className="w-28 text-xs text-gray-500 font-sans">{label}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className={`h-3 rounded-full ${tone}`} style={{ width: `${widthPercent}%` }} />
            </div>
            <span className="w-10 text-right text-xs font-semibold text-gray-700 font-sans">{value}</span>
        </div>
    );
}

Bar.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    tone:  PropTypes.string.isRequired,
};

function FunnelStrip({ funnel }) {
    if (!funnel) {
        return <p className="text-xs text-gray-400 font-sans italic mt-2">No funnel recorded for this line.</p>;
    }
    const found = funnel.gate?.in ?? 0;
    const passed = funnel.gate?.passed ?? 0;
    const rejectedEntries = Object.entries(funnel.gate?.rejected || {}).sort((a, b) => b[1] - a[1]);
    const winner = funnel.select?.winner;
    const result = funnel.result || {};
    const isFailed = result.status && result.status !== "READY";

    return (
        <div className="mt-3 flex flex-col gap-3">
            <div className="flex flex-col gap-2">
                <Bar label="Candidates found" value={found} total={found} tone="bg-blue-400" />
                <Bar label="Passed gate" value={passed} total={found} tone="bg-green-500" />
                <Bar label="Rejected" value={found - passed} total={found} tone="bg-red-400" />
                <Bar label="Winner" value={winner ? 1 : 0} total={Math.max(found, 1)} tone="bg-[#069079]" />
            </div>

            {rejectedEntries.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {rejectedEntries.map(([reason, count]) => (
                        <span key={reason} className="px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-[11px] font-semibold font-sans">
                            {reason} × {count}
                        </span>
                    ))}
                </div>
            )}

            {winner && (
                <p className="text-xs text-gray-600 font-sans">
                    <span className="font-semibold">Winner ({funnel.select.policy}):</span>{" "}
                    {winner.vendor ? `${winner.vendor} · ` : ""}{winner.name} · ₹{winner.perUnitPrice} / unit
                    <span className="text-gray-400"> · pool of {funnel.select.pool}</span>
                </p>
            )}

            {winner?.matchLevel === "EXACT" && funnel.select.exactMatched > 0 && (
                <p className="text-xs font-sans text-gray-500">
                    {funnel.select.exactMatched} of {funnel.select.pool} passing candidates are exact matches (same release type and
                    variant as the cart item); the winner was chosen from those.
                </p>
            )}

            {winner?.matchLevel === "PROBABLE" && (
                <p className="text-xs font-sans text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    <span className="font-semibold">No exact match:</span> every passing candidate carries a release type or
                    variant the cart item does not, so the winner is a probable match. Check it.
                </p>
            )}

            {winner?.saltMatch === true && (
                <p className="text-xs font-sans text-[#069079] bg-green-50 rounded-lg px-3 py-2">
                    <span className="font-semibold">Promoted by salt match:</span>{" "}
                    its salt equals the prescribed salt, so it was chosen from the {funnel.select.saltMatched} salt-matching
                    candidate{funnel.select.saltMatched === 1 ? "" : "s"} instead of the whole pool of {funnel.select.pool}.
                </p>
            )}

            {isFailed && (
                <p className="text-xs text-red-600 font-sans font-semibold">
                    Failed at {STAGE_LABEL[result.failedStage] || result.failedStage}: {result.reason}
                </p>
            )}

            {funnel.billing && (
                <p className="text-[11px] text-gray-400 font-sans">
                    Billing engine: {funnel.billing.engine} · qty {funnel.billing.billableQuantity ?? "—"} · ₹{funnel.billing.perUnitPrice ?? "—"} / unit
                </p>
            )}

            {(funnel.gate?.rejectedSamples || []).length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-sans font-semibold mb-1">Sample rejected candidates</p>
                    <ul className="flex flex-col gap-1">
                        {funnel.gate.rejectedSamples.map((sample, i) => (
                            <li key={i} className="text-xs font-sans bg-gray-50 rounded px-2 py-1">
                                <span className="font-semibold capitalize">{sample.source}</span>
                                {sample.vendor ? ` · ${sample.vendor}` : ""} — {sample.name}
                                <span className="text-red-500"> · {sample.reason}</span>
                                {sample.detail && <span className="text-gray-400"> ({sample.detail.join("; ")})</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

FunnelStrip.propTypes = { funnel: PropTypes.object };
FunnelStrip.defaultProps = { funnel: null };

export default FunnelStrip;
